import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Tag, Ticket } from 'lucide-react';
import { fmtRM2 } from '../data/mockData';
import { useApp } from '../context/AppContext';
import { ProductCard } from '../components/commerce/ProductCard';

const TABS = [
  { id: 'all', label: 'All' },
  { id: 'food', label: 'Food', cats: ['Local Food', 'Food & Beverage', 'Bakery'] },
  { id: 'fashion', label: 'Fashion', cats: ['Fashion'] },
  { id: 'local', label: 'Local', local: true },
];

export default function PromotionsPage() {
  const [tab, setTab] = useState('all');
  const { campaign, customProducts, vouchers } = useApp();
  const active = TABS.find((t) => t.id === tab);
  let items = customProducts.filter((p) => (p.badges || []).includes('SALE'));
  if (active.cats) items = items.filter((p) => active.cats.includes(p.category));
  if (active.local) items = customProducts.filter((p) => p.madeInKelantan);
  if (tab === 'all') items = customProducts.filter((p) => (p.badges || []).length > 0);

  return (
    <div data-testid="promotions-page" className="mx-auto max-w-[1280px] px-4 py-10">
      <h1 className="font-display text-3xl font-semibold text-kelate-darkwood sm:text-4xl">Promotions</h1>
      <p className="mt-1 text-sm text-kelate-soft">Flash deals, store promotions, vouchers and campaigns.</p>

      <div className="mt-6 flex gap-1 overflow-x-auto border-b border-kelate-line" data-testid="promo-tabs">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`promo-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm ${tab === t.id ? 'border-b-2 border-kelate-darkwood font-semibold text-kelate-darkwood' : 'text-kelate-soft'}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-6 grid gap-3 sm:grid-cols-3" data-testid="voucher-list">
        {vouchers.filter((v) => v.active).map((v) => (
          <div key={v.code} className="flex items-center gap-3 rounded-2xl border border-dashed border-kelate-wood bg-kelate-surface p-4 shadow-[0_1px_3px_rgba(81,68,55,0.06)]" data-testid={`voucher-${v.code.toLowerCase()}`}>
            <Ticket size={20} className="shrink-0 text-kelate-wood" />
            <div className="min-w-0">
              <div className="text-sm font-bold tracking-wide text-kelate-darkwood">{v.code}</div>
              <div className="text-xs text-kelate-soft">{v.type === 'PERCENT' ? `${v.value}% off` : `${fmtRM2(v.value)} off`}{v.minSpend > 0 ? ` · min spend ${fmtRM2(v.minSpend)}` : ''}{v.scope === 'MERCHANT' ? ` · ${v.merchantName} only` : ' · all stores'}</div>
              <div className="mt-0.5 text-[10px] uppercase tracking-wider text-kelate-sage">{v.scope === 'PLATFORM' ? 'Platform funded' : 'Merchant funded'}</div>
            </div>
          </div>
        ))}
        {vouchers.filter((v) => v.active).length === 0 && <p className="text-sm text-kelate-soft" data-testid="voucher-empty">No active vouchers right now — check back during campaigns.</p>}
      </div>

      {campaign.active && (
        <Link to="/campaign/food-week" data-testid="campaign-banner" className="mt-6 flex items-center justify-between gap-4 rounded-3xl bg-kelate-darkwood p-5 shadow-[0_10px_28px_rgba(81,68,55,0.2)]">
          <div className="flex items-center gap-3">
            <Tag size={18} className="text-kelate-wood" />
            <div>
              <div className="font-display text-lg font-semibold text-kelate-surface">{campaign.title}</div>
              <div className="text-xs text-[#CBBFA9]">{campaign.headline}</div>
            </div>
          </div>
          <span className="rounded-xl bg-kelate-surface px-3 py-1.5 text-xs font-semibold text-kelate-darkwood">{campaign.cta}</span>
        </Link>
      )}

      <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
        {items.map((p) => <ProductCard key={p.id} product={p} />)}
      </div>
    </div>
  );
}

export function CampaignPage() {
  const { campaign, customProducts } = useApp();
  const items = customProducts.filter((p) => ['Local Food', 'Food & Beverage', 'Bakery'].includes(p.category));
  return (
    <div data-testid="campaign-page" className="mx-auto max-w-[1280px] px-4 py-10">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-kelate-wood">Campaign</p>
      <h1 className="mt-2 font-display text-3xl font-semibold text-kelate-darkwood sm:text-4xl">{campaign.title}</h1>
      <p className="mt-2 max-w-lg text-lg font-medium text-kelate-ink">{campaign.headline}</p>
      <p className="mt-1 max-w-lg text-sm text-kelate-soft">{campaign.description} Pay cash, TNG QR or bank transfer at your door.</p>
      {!campaign.active && <p className="mt-3 rounded-2xl bg-secondary/60 px-4 py-2 text-xs text-kelate-soft">This campaign has ended. Deals below remain available from merchants.</p>}
      <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
        {items.map((p) => <ProductCard key={p.id} product={p} />)}
      </div>
    </div>
  );
}
